import { useCallback, useEffect, useRef, useState } from 'react';
import {
  useGetAlertsPagePayloadLazyQuery,
  useGetSavedSegmentsLazyQuery,
  useGetSessionsLazyQuery,
  useGetVisualizationsLazyQuery,
} from '@gonfalon/observability-graphql/hooks';
import { SavedSegmentEntityType } from '@gonfalon/observability-graphql/schemas';
import { getFeatureFlagsQuery } from '@gonfalon/rest-api/queries/flags';
import { useQueryClient } from '@tanstack/react-query';
import moment from 'moment';

import { buildFlagContextResource, flagPagePath } from '../../../flagContext';
import type { Resource, ResourceOrQuery } from '../../../resources';

const TIME_FORMAT = 'YYYY-MM-DDTHH:mm:ss.000000000Z';
const SEARCH_RESULT_COUNT = 10;
/**
 * Graphs aren't searchable on their own — they only come back nested under a
 * dashboard — so we pull a wider page of dashboards and filter their graphs by
 * title client-side. Bounded so the picker stays responsive; a dedicated
 * backend graph search would lift this ceiling.
 */
const GRAPH_DASHBOARD_SCAN_COUNT = 50;

function buildDateRange() {
  return {
    start_date: moment().subtract(24, 'hours').format(TIME_FORMAT),
    end_date: moment().format(TIME_FORMAT),
  };
}

export function useResourceSearch(projectId: string, projectKey: string) {
  const [results, setResults] = useState<ResourceOrQuery[]>([]);
  const [loading, setLoading] = useState(false);
  const searchIdRef = useRef(0);
  const queryClient = useQueryClient();

  const [fetchSessions] = useGetSessionsLazyQuery({ fetchPolicy: 'network-only' });
  const [fetchVisualizations] = useGetVisualizationsLazyQuery({ fetchPolicy: 'network-only' });
  const [fetchAlerts] = useGetAlertsPagePayloadLazyQuery({ fetchPolicy: 'network-only' });
  const [fetchSavedSegments] = useGetSavedSegmentsLazyQuery({ fetchPolicy: 'network-only' });

  const searchLatest = async (resourceType: Resource, query: string) => {
    const id = ++searchIdRef.current;
    setLoading(true);

    let didThrow = false;
    let thrown: unknown;
    try {
      let mapped: ResourceOrQuery[] = [];

      if (resourceType === 'Sessions') {
        const { data } = await fetchSessions({
          variables: {
            project_id: projectId,
            count: SEARCH_RESULT_COUNT,
            params: { query, date_range: buildDateRange() },
            sort_desc: true,
          },
        });
        mapped =
          data?.sessions?.sessions?.map((session) => ({
            id: session.secure_id,
            payload: session,
            type: 'Sessions' as const,
          })) ?? [];
      } else if (resourceType === 'Alert') {
        const { data } = await fetchAlerts({
          variables: { project_id: projectId },
        });
        const lowerQuery = query.toLowerCase();
        mapped =
          data?.alerts
            ?.filter((alert): alert is NonNullable<typeof alert> => alert !== null && alert !== undefined)
            .filter((alert) => !lowerQuery || alert.name.toLowerCase().includes(lowerQuery))
            .slice(0, SEARCH_RESULT_COUNT)
            .map((alert) => ({
              id: String(alert.id),
              payload: { ...alert, alert_id: alert.id },
              type: 'Alert' as const,
            })) ?? [];
      } else if (resourceType === 'SavedSearch') {
        const entityTypes = [
          SavedSegmentEntityType.Error,
          SavedSegmentEntityType.Log,
          SavedSegmentEntityType.Session,
          SavedSegmentEntityType.Trace,
        ];
        const fetchResults = await Promise.all(
          entityTypes.map((entityType) =>
            fetchSavedSegments({
              variables: { project_id: projectId, entity_type: entityType },
            }).then(({ data }) => ({ data, entityType })),
          ),
        );
        const allSegments = fetchResults.flatMap(
          ({ data, entityType }) =>
            data?.saved_segments
              ?.filter((s): s is NonNullable<typeof s> => s !== null && s !== undefined)
              .map((segment) => ({
                id: String(segment.id),
                payload: { ...segment, saved_search_id: segment.id, entity_type: entityType },
                type: 'SavedSearch' as const,
              })) ?? [],
        );
        const lowerQuery = query.toLowerCase();
        mapped = allSegments
          .filter((s) => !lowerQuery || s.payload.name.toLowerCase().includes(lowerQuery))
          .slice(0, SEARCH_RESULT_COUNT);
      } else if (resourceType === 'Graph') {
        const { data } = await fetchVisualizations({
          variables: {
            project_id: projectId,
            input: '',
            count: GRAPH_DASHBOARD_SCAN_COUNT,
            offset: 0,
          },
        });
        const lowerQuery = query.toLowerCase();
        mapped =
          data?.visualizations?.results
            ?.flatMap((viz) =>
              viz.graphs.map((graph) => ({
                id: String(graph.id),
                payload: {
                  ...graph,
                  graph_id: graph.id,
                  graph_title: graph.title,
                  chart_type: graph.type,
                  dashboard_id: viz.id,
                  dashboard_name: viz.name,
                },
                type: 'Graph' as const,
              })),
            )
            .filter((graph) => !lowerQuery || (graph.payload.graph_title ?? '').toLowerCase().includes(lowerQuery))
            .slice(0, SEARCH_RESULT_COUNT) ?? [];
      } else if (resourceType === 'Dashboard') {
        const { data } = await fetchVisualizations({
          variables: {
            project_id: projectId,
            input: query,
            count: SEARCH_RESULT_COUNT,
            offset: 0,
          },
        });
        mapped =
          data?.visualizations?.results?.map((viz) => ({
            id: String(viz.id),
            payload: viz,
            type: 'Dashboard' as const,
          })) ?? [];
      } else if (resourceType === 'Flag' && projectKey) {
        const data = await queryClient.fetchQuery(
          getFeatureFlagsQuery({
            projectKey,
            query: {
              limit: SEARCH_RESULT_COUNT,
              sort: 'name',
              filter: query ? { query } : undefined,
            },
          }),
        );
        mapped =
          data.items?.map((flag) =>
            buildFlagContextResource({
              projectKey,
              flagKey: flag.key,
              name: flag.name || flag.key,
              url: flagPagePath(projectKey, flag.key),
            }),
          ) ?? [];
      }

      // Only apply if this is still the latest search
      if (id === searchIdRef.current) {
        setResults(mapped);
      }
    } catch (caught) {
      didThrow = true;
      thrown = caught;
    }
    if (id === searchIdRef.current) {
      setLoading(false);
    }
    if (didThrow) {
      throw thrown;
    }
  };
  const searchLatestRef = useRef(searchLatest);
  useEffect(() => {
    searchLatestRef.current = searchLatest;
  });
  const search = useCallback((resourceType: Resource, query: string) => {
    return searchLatestRef.current(resourceType, query);
  }, []);

  const clearResults = useCallback(() => {
    searchIdRef.current++;
    setResults([]);
    setLoading(false);
  }, []);

  return { results, loading, search, clearResults };
}
